import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import VerticalFoldingMenu from "../../VerticalFoldingMenu";
import { SidebarOption } from "../../SidebarOption";
import { useBoundarySelectContext } from "../../../boundarySelection/BoundarySelectContext";
import { useTenantContext } from "../../../../context/Tenant/TenantContext";
import { useLayerContext } from "../../../../context/Layer/LayerContext";
import { Sidebar } from "../../sidebar";

const options: SidebarOption[] = [
  {
    label: "POI",
    children: [
      {
        label: "OS POI",
        url: "/os-poi",
        id: "OS:POI",
      },
      {
        label: "OSM POI",
        url: "/osm-poi",
        id: "OSM:POI",
      },
    ],
  },
  {
    label: "Public Transport",
    children: [
      {
        label: "TimeTables",
        url: "/public-transport",
        id: "Basemap:PublicTransport",
      },
      {
        label: "Bus Route Lines",
        url: "/uk-route-Lines",
        id: "Basemap:RouteLine",
      },
      {
        label: "NaPTAN",
        url: "naptan",
        id: "Basemap:Naptan",
      },
    ],
  },
  {
    label: "Roads",
    children: [
      {
        label: "OS Highways",
        url: "os-highways",
        id: "OS:Highways",
      },
      {
        label: "OS MRN",
        url: "os-mrn",
        id: "OS:MRN",
      },
      {
        label: "OS OpenRoads",
        url: "os-openroads",
        id: "OS:OpenRoads",
      },
      {
        label: "OSM Roads",
        url: "osm-roads",
        id: "OSM:Roads",
      },
    ],
  },
  {
    label: "GB Census",
    children: [
      {
        url: "/uk-census",
        id: "Basemap:Census",
        label: "Census",
      },
      {
        url: "/population-centroids",
        id: "Basemap:PopulationWeightedCentroids",
        label: "Weighted Centroids",
      },
    ],
  },
  {
    label: "OS Code-Point Open",
    url: "os-codepoints",
    id: "OS:CodePoints",
  },
  {
    label: "US Census",
    url: "us-census",
    id: "US:Census",
  },
  {
    url: "/inrix-trip",
    id: "Inrix:Trip",
    label: "Trip",
  },
  {
    url: "/inrix-volumes",
    id: "Inrix:Volumes",
    label: "Volumes",
  },
  {
    url: "/inrix-speed-profiles",
    id: "Inrix:SpeedProfiles",
    label: "Speed Profiles",
  },
];

const LayerSelection = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { layers } = useLayerContext();
  const { availableFeatureTypes } = useBoundarySelectContext();
  const [sidebarOptions, setSidebarOptions] = useState<SidebarOption[]>([]);

  const { boundaries } = useBoundarySelectContext();
  const showSidebar = boundaries.length > 0;

  const toggleLayers = useCallback(
    (options: SidebarOption[]) => {
      options.forEach((option) => {
        if (option.id !== undefined) {
          const layer = layers.find((l) => l.name === option.id);
          const available = availableFeatureTypes.some((f) => f === option.id);
          option.isDisabled = !available || !layer;
          option.isPremium = !!(layer && layer.isPremium);
        }

        if (option.children) {
          toggleLayers(option.children);
        }
      });
    },
    [layers, availableFeatureTypes],
  );

  useEffect(() => {
    toggleLayers(options);
    setSidebarOptions([...options]);
  }, [layers, availableFeatureTypes]);

  const sidebarMenuNavigate = (url: string) => {
    navigate(url as string);
  };

  const config = useTenantContext().config;
  const hideLayers = config.hideLayers || [];
  const filteredOptions = options
    .map((option) => {
      if (option.children) {
        const filteredChildren = option.children.filter(
          (child) => !hideLayers.includes(child.id),
        );

        if (filteredChildren.length === 0) {
          return null;
        }

        return { ...option, children: filteredChildren };
      }

      return hideLayers.includes(option.id!) ? null : option;
    })
    .filter(Boolean) as SidebarOption[];

  return (
    <>
      {showSidebar && location.pathname === "/" && (
        <Sidebar>
          <VerticalFoldingMenu
            url={location.pathname}
            options={filteredOptions}
            onSelect={sidebarMenuNavigate}
          />
        </Sidebar>
      )}
    </>
  );
};

export default LayerSelection;
